import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, InputOnChangeData, Popover, PopoverSurface } from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { useLazyGetSuggestionsQuery, useLogSuggestionClickMutation } from '../../Api/Api';
import { IKeywordSuggestion, IProduct, ITermSuggestion, SuggestionClickKind } from '../../Api/Models/Products';
import { useLocalization } from '../Helpers/useLocalization';
import { ISearchBoxProps, MIN_SUGGEST_CHARS, SUGGEST_DEBOUNCE_MS, useSearchBoxValue } from './searchBoxShared';
import { columnEnabled, PRODUCTS_COLUMN, resolveAutocompleteStyles, textColumnsParam } from './autocompleteConfig';

/** A suggestion flattened out of its column, so arrow keys can walk the whole popout in one sequence. */
interface IFlatItem {
    activate: () => void;
}

/**
 * The search box with the autocomplete popout.
 *
 * Only mounted when the storefront has autocomplete switched on — SearchBox renders the plain input
 * otherwise. Everything that needs the store (the suggest query, localization) is therefore confined
 * here, which is what lets a non-adopting storefront render exactly the markup it did before.
 */
const SuggestingSearchBox: React.FC<ISearchBoxProps> = (props) => {
    const nav = useNavigate();
    const L = useLocalization();
    const { keywords, setKeywords } = useSearchBoxValue(props);
    const { onSearch, autocomplete } = props;

    const [open, setOpen] = React.useState(false);
    const [activeIndex, setActiveIndex] = React.useState(-1);
    // The element the popout hangs off. State rather than a ref so the first render after mount, when
    // the input element actually exists, re-runs and gives the popover a target.
    const [anchor, setAnchor] = React.useState<HTMLInputElement | null>(null);
    // Per-instance, because more than one of these can be on a page: a storefront with header search on
    // renders one in the header and another on the search page. Static ids would collide, which is
    // invalid HTML and, worse, silently points one bar's aria-controls/aria-activedescendant at the
    // other bar's panel.
    const panelId = React.useId();
    // Two requests against one endpoint, split by column, because their costs are nothing alike. The
    // text columns come straight out of the BFF's suggestion cache; the product column re-hydrates its
    // ids upstream on EVERY request (prices and stock are per-shopper and time-varying, so they cannot
    // be cached). Asking for them together made the whole popout wait on the expensive half.
    const [fetchTerms, termsQuery] = useLazyGetSuggestionsQuery();
    const [fetchProducts, productsQuery] = useLazyGetSuggestionsQuery();
    const [logClick] = useLogSuggestionClickMutation();

    // Per-bar columns. Undefined means "not configured", which reads as ON.
    const showKeywords = columnEnabled(autocomplete?.columns, 'keywords');
    const showManufacturers = columnEnabled(autocomplete?.columns, 'manufacturers');
    const showTags = columnEnabled(autocomplete?.columns, 'tags');
    const showProducts = columnEnabled(autocomplete?.columns, 'products');
    // Tells the BFF what this bar renders so it can skip work it will not show.
    const textColumns = React.useMemo(() => textColumnsParam(autocomplete?.columns), [autocomplete]);
    // Resolved once per config rather than per row: every row spreads `styles.item`, and the popout can
    // hold thirty of them.
    const styles = React.useMemo(() => resolveAutocompleteStyles(autocomplete), [autocomplete]);

    // Only a keystroke may open the popout. The value also changes programmatically — choosing a
    // suggestion writes the term back through onValueChange, and the search page then re-syncs it from
    // the URL it navigated to — and the debounced effect below cannot tell those apart from typing.
    // Without this the popout re-opened over the results the shopper had just chosen, and fired a
    // pointless suggest request to do it.
    const typedRef = React.useRef(false);
    // The prefix the in-flight requests belong to. A response can outlive what asked for it: clear the
    // box while one is in flight and its `.then` would re-open the popout over an empty input, showing
    // suggestions for a term no longer on screen. Cleared when the input drops below the minimum, so a
    // superseded response can never re-open anything.
    const pendingQueryRef = React.useRef('');

    const close = React.useCallback(() => {
        setOpen(false);
        setActiveIndex(-1);
        typedRef.current = false;
    }, []);

    const search = React.useCallback((submitted: string) => {
        close();
        if (onSearch) onSearch(submitted);
        else nav(`/search?keywords=${encodeURIComponent(submitted)}`);
    }, [nav, onSearch, close]);

    // Debounced fetch. The timer is cleared on every keystroke and on unmount, so typing straight
    // through never fires an intermediate request and navigating away cannot leave one in flight
    // against an unmounted popout.
    React.useEffect(() => {
        const trimmed = keywords.trim();
        if (trimmed.length < MIN_SUGGEST_CHARS) {
            pendingQueryRef.current = '';
            setOpen(false);
            return;
        }
        // Closing stays unconditional above (a programmatic clear should still dismiss the popout);
        // only re-opening requires that the shopper actually typed.
        if (!typedRef.current) return;
        const timer = window.setTimeout(() => {
            pendingQueryRef.current = trimmed;
            // Fired together and resolved independently: the popout opens on whichever arrives first,
            // which is the text half in every realistic case. On a cold prefix both miss the BFF's
            // suggestion cache and each runs the prefix query once — one extra cheap Elastic hit per
            // never-before-typed prefix, in exchange for never blocking the popout on hydration.
            if (showProducts) {
                fetchProducts({ q: trimmed, columns: PRODUCTS_COLUMN })
                    .unwrap()
                    // Only actually opens the popout for a bar configured with the product column
                    // alone; otherwise the text request has already done it.
                    .then(() => { if (pendingQueryRef.current === trimmed) setOpen(true); })
                    // A failed product column must not take down text columns that arrived fine.
                    .catch(() => {});
            }
            if (textColumns) {
                fetchTerms({ q: trimmed, columns: textColumns })
                    .unwrap()
                    .then(() => { if (pendingQueryRef.current === trimmed) setOpen(true); })
                    // A failed suggest request is not worth surfacing: the shopper can still search, and
                    // an error over a search box would be far more disruptive than a missing popout.
                    .catch(() => setOpen(false));
            }
        }, SUGGEST_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [keywords, fetchTerms, fetchProducts, textColumns, showProducts]);

    // currentData, NOT data: `data` deliberately re-uses the result of the PREVIOUS arguments while a
    // new request is in flight, which here means the popout renders the last prefix's suggestions under
    // the new one — search "micro", then "lcars", and the micro results are what appears. currentData is
    // empty until the response for the current prefix lands, so the popout can only ever show results
    // for what was actually typed. It does not blank between keystrokes: the args only change when the
    // debounced request fires, so this holds the last resolved payload until then.
    const results = termsQuery.currentData;
    // Memoized per column: these feed the flatItems memo below, and rebuilding the arrays every render
    // would make it recompute constantly while the shopper arrows through the list.
    const keywordItems = React.useMemo(
        () => (showKeywords ? (results?.keywords ?? []) : []), [showKeywords, results]);
    const manufacturerItems = React.useMemo(
        () => (showManufacturers ? (results?.manufacturers ?? []) : []), [showManufacturers, results]);
    const tagItems = React.useMemo(
        () => (showTags ? (results?.tags ?? []) : []), [showTags, results]);

    // Same reasoning, and it is also what keeps the two halves consistent with each other: both requests
    // are triggered with the same prefix in the same tick, so keying each column to its own current
    // arguments means neither can display a different prefix's answer. The product column is simply
    // empty for the gap between the two responses — the cost of not making the text columns wait on the
    // hydration.
    const productItems = React.useMemo(
        () => (showProducts ? (productsQuery.currentData?.products ?? []) : []),
        [showProducts, productsQuery.currentData]);

    const hasAny = keywordItems.length > 0 || manufacturerItems.length > 0
        || tagItems.length > 0 || productItems.length > 0;

    // Reports which suggestion was taken, against the prefix the popout was served for — not what is
    // currently typed, since choosing a suggestion rewrites the box. Fire-and-forget and never awaited:
    // the shopper is navigating, and a failed analytics write must not be visible to them.
    const reportClick = React.useCallback((
        kind: SuggestionClickKind, term: string, position: number, productId?: string) => {
        const q = pendingQueryRef.current;
        if (!q) return;
        logClick({ q, term, kind, position, productId }).unwrap().catch(() => {});
    }, [logClick]);

    // The interest signal for a product reached from the popout, carried as ROUTER STATE — which is
    // what ProductPage reads (`location.state`), exactly as ProductCard, SearchPage and the cart write
    // it. It never reads the query string, so the `?source=autocomplete&searchContext=` this used to
    // append went nowhere: the view was recorded with no source at all, and the params were left in a
    // URL the shopper could bookmark or share. Shared by both ways out of the popout — a product card
    // and a part-number suggestion are the same signal.
    const interestState = React.useMemo(
        () => ({ source: 'autocomplete', searchContext: { keywords: keywords.trim() } }),
        [keywords]);

    const goToKeyword = React.useCallback((item: IKeywordSuggestion, position: number) => {
        // The first column mixes three kinds that navigate differently; each is reported as itself so a
        // taxonomy path and a loose keyword phrase can be judged separately.
        reportClick(item.kind as SuggestionClickKind, item.text, position, item.productId);
        close();
        if (item.kind === 'partNumber' && item.productId) {
            nav(`/products/${encodeURIComponent(item.productId)}`, { state: interestState });
            return;
        }
        if (item.kind === 'taxonomy') {
            const params = new URLSearchParams();
            if (item.segment) params.set('segment', item.segment);
            if (item.category) params.set('category', item.category);
            if (item.subCategory) params.set('subcategory', item.subCategory);
            nav(`/search?${params.toString()}`);
            return;
        }
        setKeywords(item.text);
        search(item.text);
    }, [nav, search, setKeywords, close, interestState, reportClick]);

    const goToFiltered = React.useCallback((
        param: string, value: string, kind: SuggestionClickKind, position: number) => {
        reportClick(kind, value, position);
        close();
        nav(`/search?${param}=${encodeURIComponent(value)}`);
    }, [nav, close, reportClick]);

    const goToProduct = React.useCallback((product: IProduct, position: number) => {
        // Falls back to the id: a product with no name is still a click worth counting, and the id is
        // what a ranker joins on anyway.
        reportClick('product', product.name ?? product.id, position, product.id);
        close();
        nav(`/products/${encodeURIComponent(product.id)}`, { state: interestState });
    }, [nav, interestState, close, reportClick]);

    // One flat sequence across all four columns, in visual order, so ArrowDown walks the popout the way
    // it reads rather than jumping between columns.
    const flatItems = React.useMemo<IFlatItem[]>(() => [
        // Position is 1-based WITHIN its column, matching what the click handlers report — ranking is
        // decided per column, so a rank is only meaningful against the entries competing for that slot.
        ...keywordItems.map((item, i) => ({ activate: () => goToKeyword(item, i + 1) })),
        ...manufacturerItems.map((item, i) => ({ activate: () => goToFiltered('manufacturer', item.text, 'manufacturer', i + 1) })),
        // 'tag', not 'tagFilters': the search page reads params.getAll('tag'), and tagFilters is only
        // its internal variable name (and the BFF request field). The wrong one is not merely ignored —
        // it is absent from SEARCH_FILTER_PARAMS too, so it survives nowhere and the shopper lands on
        // unfiltered results with no sign anything was dropped.
        ...tagItems.map((item, i) => ({ activate: () => goToFiltered('tag', item.text, 'tag', i + 1) })),
        ...productItems.map((item, i) => ({ activate: () => goToProduct(item, i + 1) })),
    ], [keywordItems, manufacturerItems, tagItems, productItems, goToKeyword, goToFiltered, goToProduct]);

    const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            // A highlighted suggestion wins over the raw text: the shopper arrowed to it deliberately.
            if (open && activeIndex >= 0 && activeIndex < flatItems.length) {
                e.preventDefault();
                flatItems[activeIndex].activate();
                return;
            }
            search(keywords);
            return;
        }
        if (!open || flatItems.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(i => (i + 1) % flatItems.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(i => (i <= 0 ? flatItems.length - 1 : i - 1));
        } else if (e.key === 'Escape') {
            close();
        }
    };

    const input = <Input
        // Anchors the popout. The field is NOT a PopoverTrigger: a trigger runs its child through
        // useARIAButtonProps, which gives a non-<button> element button semantics — including a keydown
        // that calls preventDefault() on Space to stop a button scrolling the page. On a text input that
        // ate the space bar, so multi-word searches could not be typed at all. `disableButtonEnhancement`
        // does not prevent it (it only selects which props object is passed INTO that call), and
        // intercepting Space ourselves cannot help either, since Fluent runs our handler first and then
        // skips its own branch only when the default was already prevented — which would suppress the
        // character just the same. Anchoring by positioning target instead drops the button semantics
        // entirely; Fluent forwards this ref to the same <input> the trigger cloned onto, so the popout
        // sits exactly where it did. It also lets the combobox roles below stand, rather than being
        // overwritten with role="button".
        ref={setAnchor}
        style={props.style}
        placeholder={props.placeholder}
        size='large'
        value={keywords}
        onChange={(_e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
            typedRef.current = true;
            setKeywords(data.value);
            setActiveIndex(-1);
        }}
        onKeyDown={handleKeywordKeyDown}
        role='combobox'
        aria-expanded={open && hasAny}
        aria-autocomplete='list'
        aria-controls={panelId}
        aria-activedescendant={activeIndex >= 0 ? `${panelId}-item-${activeIndex}` : undefined}
        contentAfter={<SearchRegular onClick={() => search(keywords)} style={{ cursor: 'pointer' }} />}
    />;

    let runningIndex = -1;
    const nextIndex = () => ++runningIndex;

    const renderRow = (key: string, index: number, onActivate: () => void, content: React.ReactNode) =>
        <li
            key={key}
            id={`${panelId}-item-${index}`}
            role='option'
            aria-selected={activeIndex === index}
            className={`sf-suggest-item${activeIndex === index ? ' sf-suggest-item-active' : ''}`}
            style={styles.item}
            // Mouse down rather than click: the input loses focus on mousedown, which would close the
            // popover before a click ever landed.
            onMouseDown={(e) => { e.preventDefault(); onActivate(); }}
            onMouseEnter={() => setActiveIndex(index)}
        >
            {content}
        </li>;

    const renderTermColumn = (heading: string, items: ITermSuggestion[], param: string, kind: SuggestionClickKind) => {
        if (items.length === 0) return null;
        return <div className='sf-suggest-column'>
            <div className='sf-suggest-heading' style={styles.heading}>{heading}</div>
            <ul className='sf-suggest-list' role='group' aria-label={heading}>
                {items.map((item, i) => {
                    const index = nextIndex();
                    return renderRow(`${param}-${i}`, index, () => goToFiltered(param, item.text, kind, i + 1),
                        <>
                            <span className='sf-suggest-text'>{item.text}</span>
                            {item.count > 0 && <span className='sf-suggest-count' style={styles.count}>{item.count}</span>}
                        </>);
                })}
            </ul>
        </div>;
    };

    return <>
        {input}
        <Popover
        open={open && hasAny}
        onOpenChange={(_e, data) => setOpen(data.open)}
        positioning={{ target: anchor, position: 'below', align: 'start' }}
        // The input must keep focus while the popout is open, or typing stops working the moment
        // suggestions appear.
        trapFocus={false}
    >
        <PopoverSurface id={panelId} className='sf-suggest-panel' role='listbox'
            // Its own label rather than the input's placeholder: this is the listbox, not the search
            // box, and giving two different elements the same name is what makes a screen reader
            // ambiguous rather than consistent.
            aria-label={L('localization_suggest_panel', 'Search suggestions')}
            // The highlight colour has to travel as a CSS variable: there is no inline :hover, and the
            // keyboard highlight is class-driven. Everything else is applied inline per part.
            style={styles.panel}>
            <div className='sf-suggest-columns'>
                {keywordItems.length > 0 && <div className='sf-suggest-column'>
                    <div className='sf-suggest-heading' style={styles.heading}>{L('localization_suggest_keywords', 'Suggestions')}</div>
                    <ul className='sf-suggest-list' role='group'>
                        {keywordItems.map((item, i) => {
                            const index = nextIndex();
                            return renderRow(`kw-${i}`, index, () => goToKeyword(item, i + 1),
                                <>
                                    <span className='sf-suggest-text'>
                                        {item.kind === 'taxonomy' && item.segment
                                            ? [item.segment, item.category, item.subCategory].filter(Boolean).join(' › ')
                                            : item.text}
                                    </span>
                                    {item.count > 0 && <span className='sf-suggest-count' style={styles.count}>{item.count}</span>}
                                </>);
                        })}
                    </ul>
                </div>}

                {renderTermColumn(L('localization_suggest_manufacturers', 'Manufacturers'), manufacturerItems, 'manufacturer', 'manufacturer')}
                {renderTermColumn(L('localization_suggest_tags', 'Tags'), tagItems, 'tag', 'tag')}

                {productItems.length > 0 && <div className='sf-suggest-column sf-suggest-products'>
                    <div className='sf-suggest-heading' style={styles.heading}>{L('localization_suggest_products', 'Products')}</div>
                    <ul className='sf-suggest-list' role='group'>
                        {productItems.map((product, i) => {
                            const index = nextIndex();
                            return renderRow(`pr-${i}`, index, () => goToProduct(product, i + 1),
                                <div className='sf-suggest-product' style={styles.product}>
                                    {product.image
                                        ? <img src={product.image} alt='' className='sf-suggest-thumb' loading='lazy' style={styles.thumbnail} />
                                        : <div className='sf-suggest-thumb sf-suggest-thumb-empty' aria-hidden style={styles.thumbnail} />}
                                    <div className='sf-suggest-product-text'>
                                        <div className='sf-suggest-product-name'>{product.name}</div>
                                        {product.manufacturer && <div className='sf-suggest-product-mfr'>{product.manufacturer}</div>}
                                    </div>
                                </div>);
                        })}
                    </ul>
                </div>}
            </div>
        </PopoverSurface>
        </Popover>
    </>;
};

export default SuggestingSearchBox;
